/* Segal name necklace — PLATE MODE (2026-09-04; outer-letter chain anchors 2026-09-14).
 *
 * Why this file exists. The image model cannot be trusted to place the chain: on 8 of 21
 * generated gold names Yaniv rejected, the right chain sat on top of the last letter while the
 * letter's exit tail ran on past it, and the AI judge could not see it either. So the browser now
 * draws the necklace first — the name in the approved font for its script, joined into ONE
 * castable piece (bridges for dots and accents, a base bar for scripts whose letters never
 * touch), with the chain drawn from the true outer top corners — and sends that drawing
 * (`plateB64`) with the /name-request. The Worker only turns the drawing into gold and checks
 * the result against it. Same path for every script, Latin included.
 *
 * Loads right after name-necklace-family.js and, like it, patches window.fetch: a POST to the
 * request endpoint for the plain family gets a plate attached on the way out. If drawing fails
 * (font blocked, old browser) a Latin name still goes without a plate and the Worker falls back
 * to the prompted flow; a non-Latin name is refused by the Worker with the contact message.
 *
 * Exposes window.SegalPlate = { render, forName, supported, scriptOf, hash } for name-necklace-ui.js.
 */
(function () {
  'use strict';
  const cfg = window.SegalNameNecklace || {};
  const GOLD = '#B78B43', BG = '#FFFFFF', W = 1024, H = 1024;

  // One row per supported script. Fonts are the ones Yaniv chose on 2026-09-04 and approved from
  // cast samples. `bar`: base bar under letters that never touch. `tight`: pull tracking in until
  // glyphs touch (Hebrew: "get the letters stick to each other, no need the line down there").
  // `stroke`: thicken thin fonts (fraction of the font size). `anchor`: where the chain meets the
  // outer letter — 'corner' = top-outer corner (a rising exit tail wins over the letter body),
  // 'top' = highest point of the OUTER part of the outer letter (Arabic - see the anchor block in render()).
  const SCRIPTS = [
    { key: 'latin',      re: /^[A-Za-zÀ-ɏḀ-ỿ’'\- ]+$/, font: 'Pacifico',            dir: 'ltr' },
    { key: 'hebrew',     re: /^[֐-׿\s'’\-]+$/,                  font: 'Suez One',            dir: 'rtl', tight: true },
    // `alphabet`: subset the font once with every letter of the script instead of per name. Google
    // Fonts serves a per-text subset, and a second name registers a second face of the same family
    // that wins for every later draw - re-typing the first name then found no glyphs in it and fell
    // back to the system font (measured 2026-09-14 while testing anchors on 20 names in one page).
    { key: 'arabic',     re: /^[؀-ۿݐ-ݿ\s\-]+$/,            font: 'Amiri',               dir: 'rtl', weight: 700, anchor: 'top',
      alphabet: 'ءآأؤإئابةتثجحخدذرزسشصضطظعغػؼؽؾؿـفقكلمنهوىيًٌٍَُِّْٰپچڤگیے -' },
    { key: 'chinese',    re: /^[一-鿿\s]+$/,                            font: 'Ma Shan Zheng',       dir: 'ltr', bar: true },
    { key: 'korean',     re: /^[가-힯\s]+$/,                            font: 'Song Myung',          dir: 'ltr', bar: true },
    { key: 'devanagari', re: /^[ऀ-ॿ\s\-]+$/,                          font: 'Eczar',               dir: 'ltr' },
    { key: 'cyrillic',   re: /^[Ѐ-ӿ\s'\-]+$/,                         font: 'Lobster',             dir: 'ltr' },
    { key: 'armenian',   re: /^[԰-֏\s\-]+$/,                          font: 'Noto Serif Armenian', dir: 'ltr', bar: true },
    { key: 'japanese',   re: /^[぀-ヿ一-鿿\s]+$/,               font: 'Kaisei Decol',        dir: 'ltr', bar: true },
    { key: 'thai',       re: /^[฀-๿\s\-]+$/,                          font: 'Itim',                dir: 'ltr', bar: true, stroke: 0.02 }
  ];
  // Chinese-only text matches both the Chinese and the Japanese row; Chinese is listed first and
  // wins, kana anywhere in the name makes it Japanese. Same order as the Worker's detectScript.
  const scriptOf = (name) => {
    const v = String(name || '').trim();
    if (!v) return null;
    for (const s of SCRIPTS) if (s.re.test(v)) return s;
    return null;
  };
  const supported = (name) => !!scriptOf(name);

  /** FNV-1a over UTF-16 code units, 8 hex chars — identical to the Worker's stableHash, so the
   *  storefront can predict the handle of a non-Latin name (`hebrew-gold-name-necklace-<hash>`). */
  const hash = (str) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i += 1) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h.toString(16).padStart(8, '0');
  };

  const fontCache = {};
  // `weight`: a real bold cut instead of a canvas stroke. 2026-09-05 (Arabic ليلى / ميسون): stroking
  // Amiri Regular to thicken it closed the gap between the two dots of ي, and one fused blob under
  // a tooth reads as ب to anyone who can read Arabic. Amiri Bold is drawn with the dots apart.
  async function loadFont(family, text, weight, alphabet) {
    if (alphabet) text = alphabet;
    const k = family + '|' + (weight || 400) + '|' + text;
    if (fontCache[k]) return fontCache[k];
    fontCache[k] = (async () => {
      const url = 'https://fonts.googleapis.com/css2?family=' + encodeURIComponent(family).replace(/%20/g, '+')
                + (weight ? ':wght@' + weight : '')
                + '&text=' + encodeURIComponent(text) + '&display=swap';
      const css = await fetch(url, { mode: 'cors' }).then(r => r.text());
      const m = css.match(/src:\s*url\(([^)]+)\)/); if (!m) throw new Error('no font url for ' + family);
      const face = new FontFace(family, 'url(' + m[1] + ')', weight ? { weight: String(weight) } : undefined);
      await face.load(); document.fonts.add(face); return face;
    })();
    return fontCache[k];
  }

  // Connected components on the alpha mask, 8-connectivity, largest first.
  function components(mask, w, h) {
    const seen = new Uint8Array(w * h); const comps = []; const stack = new Int32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      if (!mask[i] || seen[i]) continue;
      let sp = 0; stack[sp++] = i; seen[i] = 1;
      const c = { size: 0, minx: w, miny: h, maxx: 0, maxy: 0, px: [] };
      while (sp) {
        const p = stack[--sp]; const x = p % w, y = (p / w) | 0;
        c.size++; c.px.push(p);
        if (x < c.minx) c.minx = x; if (x > c.maxx) c.maxx = x; if (y < c.miny) c.miny = y; if (y > c.maxy) c.maxy = y;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue; const nx = x + dx, ny = y + dy; if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const q = ny * w + nx; if (mask[q] && !seen[q]) { seen[q] = 1; stack[sp++] = q; }
        }
      }
      comps.push(c);
    }
    comps.sort((a, b) => b.size - a.size); return comps;
  }
  function maskOf(ctx, w, h) { const d = ctx.getImageData(0, 0, w, h).data; const m = new Uint8Array(w * h); for (let i = 0; i < w * h; i++) m[i] = d[i * 4 + 3] > 40 ? 1 : 0; return m; }
  function nearest(a, b, w) {
    let best = Infinity, pa = 0, pb = 0; const ka = Math.max(1, (a.px.length / 1500) | 0), kb = Math.max(1, (b.px.length / 1500) | 0);
    for (let i = 0; i < a.px.length; i += ka) { const p = a.px[i], ax = p % w, ay = (p / w) | 0;
      for (let j = 0; j < b.px.length; j += kb) { const q = b.px[j], bx = q % w, by = (q / w) | 0; const d = (ax - bx) * (ax - bx) + (ay - by) * (ay - by); if (d < best) { best = d; pa = p; pb = q; } } }
    return { d: Math.sqrt(best), ax: pa % w, ay: (pa / w) | 0, bx: pb % w, by: (pb / w) | 0 };
  }

  async function render(opts) {
    const { family, text, dir = 'ltr', bar = false, fontSize = 300, stroke = 0, tight = false, anchor = 'corner', weight = 0, alphabet = '' } = opts;
    await loadFont(family, text, weight, alphabet);
    const c = document.createElement('canvas'); c.width = W; c.height = H; const ctx = c.getContext('2d');
    ctx.fillStyle = GOLD; ctx.strokeStyle = GOLD;
    const setFont = (sz) => { ctx.font = (weight ? weight + ' ' : '') + sz + 'px "' + family + '"'; };
    setFont(fontSize); ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.direction = dir;
    // grow-to-fill: the word spans ~66% of the plate. A short word drawn small (نور) is a squiggle
    // the model re-imagines as something else ("Lily", measured 2026-09-04).
    let size = Math.max(60, Math.min(560, Math.round(fontSize * (W * 0.66) / Math.max(1, ctx.measureText(text).width))));
    setFont(size);
    while (ctx.measureText(text).width > W * 0.72 && size > 60) { size -= 10; setFont(size); }
    ctx.lineJoin = 'round'; ctx.lineWidth = stroke && stroke < 1 ? stroke * size : stroke;
    const draw = () => { ctx.clearRect(0, 0, W, H); ctx.fillText(text, W / 2, H * 0.52); if (ctx.lineWidth > 0) ctx.strokeText(text, W / 2, H * 0.52); };
    draw();
    let mask = maskOf(ctx, W, H); let comps = components(mask, W, H); const before = comps.length;
    let spacing = 0;
    if (tight && 'letterSpacing' in ctx) {
      // 2026-09-05, נועה / שירה: pulling tracking in until every glyph merged (-22% of the size)
      // swallowed the narrow letters - the ו vanished into the ע, the י into the ר - and the
      // cast read as a different name. Tighten only a little (-6%); the bridge step below joins
      // whatever still stands apart with short bars, which is what "letters stick to each
      // other" looks like without losing a letter.
      while (comps.length > 1 && spacing > -size * 0.06) {
        spacing -= Math.max(1, Math.round(size * 0.01)); ctx.letterSpacing = spacing + 'px'; draw();
        mask = maskOf(ctx, W, H); comps = components(mask, W, H);
      }
    }
    const bboxAll = () => { let minx = W, maxx = 0, miny = H, maxy = 0; for (const k of comps) { minx = Math.min(minx, k.minx); maxx = Math.max(maxx, k.maxx); miny = Math.min(miny, k.miny); maxy = Math.max(maxy, k.maxy); } return { minx, maxx, miny, maxy }; };
    const thick = Math.max(6, Math.round(size * 0.055));
    if (bar && comps.length > 1) {
      const bb = bboxAll();
      const bottoms = comps.filter(k => k.size > comps[0].size * 0.08).map(k => k.maxy).sort((a, b) => a - b);
      const y = bottoms[Math.floor(bottoms.length / 2)] - Math.round(thick * 0.6);
      ctx.fillRect(bb.minx, y, bb.maxx - bb.minx, thick);
      mask = maskOf(ctx, W, H); comps = components(mask, W, H);
    }
    // bridge whatever still stands apart (dots, accents, the i's dot) to the main body.
    // 2026-09-05 (Arabic ليلى / ميسون): a 5%-of-size bridge with end discs, on top of a 3% stroke,
    // fused the two dots of ي into one blob - which reads as ب/ن to anyone who can read Arabic, and
    // the judge rejected ليلى three times for "letters changed". A dot is a small component: it
    // keeps its shape and hangs on a thin stem (3% of the size, ~0.7 mm at pendant scale, no discs) instead.
    let guard = 0;
    while (comps.length > 1 && guard++ < 40) {
      const main = comps[0]; let best = null, bestComp = null;
      for (let i = 1; i < comps.length; i++) { const n = nearest(main, comps[i], W); if (!best || n.d < best.d) { best = n; bestComp = comps[i]; } }
      const small = bestComp.size < main.size * 0.04;
      ctx.lineCap = 'round'; ctx.lineWidth = Math.max(4, Math.round(size * (small ? 0.03 : 0.05))); ctx.strokeStyle = GOLD;
      ctx.beginPath(); ctx.moveTo(best.ax, best.ay); ctx.lineTo(best.bx, best.by); ctx.stroke();
      if (!small) { ctx.beginPath(); ctx.arc(best.ax, best.ay, ctx.lineWidth * 0.7, 0, 7); ctx.arc(best.bx, best.by, ctx.lineWidth * 0.7, 0, 7); ctx.fill(); }
      mask = maskOf(ctx, W, H); comps = components(mask, W, H);
    }
    const after = comps.length; const bb = bboxAll();
    // Chain anchors: within the outer letter's own columns only (a neighbour's accent can never
    // win), the pixel nearest the top-outer corner, horizontal distance weighted double.
    const glyphW = (ch) => Math.max(size * 0.35, ctx.measureText(ch).width * 1.3);
    const chars = Array.from(text);
    const advAll = ctx.measureText(text).width;
    const wL = glyphW(dir === 'rtl' ? chars[chars.length - 1] : chars[0]), wR = glyphW(dir === 'rtl' ? chars[0] : chars[chars.length - 1]);
    const region = (x0, x1) => { let top = H; for (let y = bb.miny; y <= bb.maxy && top === H; y++) for (let x = x0; x <= x1; x++) if (mask[y * W + x]) { top = y; break; } return top; };
    const pick = (x0, x1, cx) => {
      const cy = region(x0, x1); let best = Infinity, bx = cx, by = cy;
      for (let y = bb.miny; y <= bb.maxy; y++) for (let x = x0; x <= x1; x++) if (mask[y * W + x]) {
        const d = anchor === 'top' ? (y - cy) * 1e6 + Math.abs(x - cx) : (x - cx) * (x - cx) + 0.25 * (y - cy) * (y - cy);
        if (d < best) { best = d; bx = x; by = y; } }
      return { x: bx, y: by };
    };
    let L = pick(bb.minx, Math.min(bb.maxx, bb.minx + Math.round(wL)), bb.minx);
    let R = pick(Math.max(bb.minx, bb.maxx - Math.round(wR)), bb.maxx, bb.maxx);
    // 2026-09-14 (Arabic ليان / جنى / سارة / هدى / مريم): glyphW() measures the ISOLATED form of the
    // outer letter and pads it 1.3x - for Amiri that region reached the middle of the word, and the
    // 'top' rule then hung BOTH chains from the tallest middle letter (ا, ل): anchors L and R came out
    // identical on ليان and جنى, and the model faithfully drew a one-point chain. Now the outer
    // letter's ink is isolated exactly: the word is drawn again with that letter replaced by a
    // zero-width joiner (so its neighbour keeps its joined form and nothing else moves) on the
    // shared edge, and the difference is the letter. Dots and other small round marks are dropped
    // (they hang on thin stems and must never carry the chain). The anchor is the highest pixel in
    // the OUTER 40% of the letter - ن's rim, ج's head, the tip of ى's or ر's tail, the rim of final
    // ل's bowl - so the piece hangs level from its two extremities, the way the hand-made Arabic
    // necklaces are rung. (The letter's own highest point - ر's head, ل's stem - sits a third of the
    // way into the word and tilts the piece.)
    if (anchor === 'top' && chars.length > 1) {
      const mc = document.createElement('canvas'); mc.width = W; mc.height = H; const m2 = mc.getContext('2d');
      m2.font = ctx.font; m2.textBaseline = 'middle'; m2.direction = dir; m2.fillStyle = GOLD;
      const ink = (str, align, x) => { m2.clearRect(0, 0, W, H); m2.textAlign = align; m2.fillText(str, x, H * 0.52); return maskOf(m2, W, H); };
      const dotish = (k) => (k.maxx - k.minx) < size * 0.16 && (k.maxy - k.miny) < size * 0.16;
      // `adv`: the letter's advance in context (Amiri moves the letters after a removed one up or
      // down - cursive attachment - so the pixel difference is wider than the letter; its advance is not)
      const outerTop = (full, part, dx, side, adv) => {
        const diff = new Uint8Array(W * H);
        for (let i = 0; i < W * H; i++) if (full[i] && !part[i]) diff[i] = 1;
        const keep = components(diff, W, H).filter(k => !dotish(k));
        if (!keep.length) return null;
        let lminx = W, lmaxx = 0; for (const k of keep) { lminx = Math.min(lminx, k.minx); lmaxx = Math.max(lmaxx, k.maxx); }
        const span = Math.min(Math.max(1, lmaxx - lminx), Math.max(adv * 1.1, size * 0.25));
        const inOuter = side === 'L' ? (x) => x <= lminx + span * 0.4 : (x) => x >= lmaxx - span * 0.4;
        let best = null;
        for (const k of keep) for (const p of k.px) {
          const x = p % W, y = (p / W) | 0;
          if (inOuter(x) && (!best || y < best.y || (y === best.y && (side === 'L' ? x < best.x : x > best.x)))) best = { x, y };
        }
        if (!best) return null;
        // the two drawings can differ by a subpixel; land on the nearest ink of the real plate
        const X = best.x + dx;
        for (let r = 0; r <= 2; r++) for (let oy = -r; oy <= r; oy++) for (let ox = -r; ox <= r; ox++) {
          const x = X + ox, y = best.y + oy;
          if (x >= bb.minx && x <= bb.maxx && y >= 0 && y < H && mask[y * W + x]) return { x, y };
        }
        return null;
      };
      // letters that never join to the letter after them (alef family, د ذ ر ز, و, ة); after one of
      // these the neighbour is already in its unjoined form and no joiner is needed
      const NOJOIN = /[ء-إاد-زوة]/;
      const ZWJ = '‍';
      const first = chars[0], beforeLast = chars[chars.length - 2];
      const minusFirst = (NOJOIN.test(first) ? '' : ZWJ) + chars.slice(1).join('');
      const minusLast  = chars.slice(0, -1).join('') + (NOJOIN.test(beforeLast) ? '' : ZWJ);
      const leftStr  = dir === 'rtl' ? minusLast : minusFirst;   // word minus the LEFT-end letter
      const rightStr = dir === 'rtl' ? minusFirst : minusLast;   // word minus the RIGHT-end letter
      const lx = 100, rx = W - 100;
      // the left-end letter: align on the RIGHT edge (shared by the rest of the word); right-end: align on the LEFT
      const l = outerTop(ink(text, 'right', rx), ink(leftStr, 'right', rx), Math.round(W / 2 + advAll / 2) - rx, 'L', advAll - ctx.measureText(leftStr).width);
      const r = outerTop(ink(text, 'left', lx),  ink(rightStr, 'left', lx),  Math.round(W / 2 - advAll / 2) - lx, 'R', advAll - ctx.measureText(rightStr).width);
      if (l) L = l;
      if (r) R = r;
    }
    // where the chain meets the word, as a share of its width - the Worker's chain audit checks the
    // finished photo against these instead of assuming the chain must sit at the very edges.
    const wordW = Math.max(1, bb.maxx - bb.minx);
    const expect = { l: Math.round((L.x - bb.minx) / wordW * 1000) / 10, r: Math.round((bb.maxx - R.x) / wordW * 1000) / 10 };
    ctx.lineWidth = Math.max(4, Math.round(size * 0.03)); ctx.lineCap = 'round'; ctx.setLineDash([ctx.lineWidth * 1.6, ctx.lineWidth * 1.1]);
    ctx.beginPath(); ctx.moveTo(L.x, L.y); ctx.lineTo(L.x - W * 0.16, 0); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(R.x, R.y); ctx.lineTo(R.x + W * 0.16, 0); ctx.stroke(); ctx.setLineDash([]);
    const out = document.createElement('canvas'); out.width = W; out.height = H; const o = out.getContext('2d');
    o.fillStyle = BG; o.fillRect(0, 0, W, H); o.drawImage(c, 0, 0);
    return { png: out.toDataURL('image/png'), before, after, size, spacing, bbox: bb, chain: { L, R }, expect };
  }

  /** Plate for a typed name, or null when the script is unsupported. */
  async function forName(name) {
    const s = scriptOf(name); if (!s) return null;
    const text = String(name).trim().replace(/\s+/g, ' ');
    return render({ family: s.font, text, dir: s.dir, bar: !!s.bar, stroke: s.stroke || 0, tight: !!s.tight, anchor: s.anchor || 'corner', weight: s.weight || 0, alphabet: s.alphabet || '' });
  }

  window.SegalPlate = { render, forName, supported, scriptOf, hash, SCRIPTS };

  // ----------------------------------------------------------- attach the plate on the way out
  (() => {
    const target = String(cfg.requestUrl || '');
    if (!target || cfg.family === 'diamond') return;   // diamond is still prompted from its pavé master
    const nativeFetch = window.fetch;
    if (!nativeFetch || nativeFetch.__segalPlatePatched) return;
    const patched = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (url.indexOf(target) === 0 && method === 'POST' && init && typeof init.body === 'string') {
          const body = JSON.parse(init.body);
          if (body && typeof body === 'object' && body.name && !body.plateB64) {
            const self = this;
            return (async () => {
              try {
                const t0 = performance.now();
                const r = await forName(body.name);
                if (r && r.png) {
                  body.plateB64 = r.png;
                  body.plateMeta = { pieces: r.before, joined: r.after, ms: Math.round(performance.now() - t0), chain: r.expect };
                  init = Object.assign({}, init, { body: JSON.stringify(body) });
                }
              } catch (_) { /* send without a plate; the Worker decides */ }
              return nativeFetch.apply(self, [input, init]);
            })();
          }
        }
      } catch (_) { /* never let this break a request */ }
      return nativeFetch.apply(this, [input, init]);
    };
    patched.__segalPlatePatched = true;
    window.fetch = patched;
  })();
})();
